import React, { useState } from "react";
import useEventListener from "../../shared/hooks/useEventListener";
import { AnimatedCursorProps } from "../../interfaces/components/animatedCursor";
import { isBrowserDefined } from "../../shared/services/browserService";
import "./styles.css";

/**
 * A custom animated cursor component that replaces the default browser cursor with a spring-animated circle.
 * The cursor responds to mouse movement with smooth spring physics and provides visual feedback for interactive elements.
 *
 * @component
 * @example
 * ```tsx
 * <AnimatedCursor
 *   color="220, 90, 90"
 *   outerAlpha={0.4}
 *   outerSize={8}
 *   outerScale={5}
 * />
 * ```
 *
 * @param {string} [color="220, 90, 90"] - RGB color values for the cursor, comma-separated
 * @param {number} [outerAlpha=0.4] - Opacity of the outer cursor circle
 * @param {number} [outerSize=8] - Size of the outer cursor circle in pixels
 * @param {number} [outerScale=5] - Scale factor for the cursor when hovering over interactive elements
 * @returns {JSX.Element} A div element representing the animated cursor
 */
const AnimatedCursor: React.FC<AnimatedCursorProps> = ({
  color = "220, 90, 90",
  outerAlpha = 0.4,
  outerSize = 8,
  outerScale = 5,
}) => {
  /** Reference to the outer cursor div element */
  const cursorOuterRef = React.useRef<HTMLDivElement>(null);
  /** Reference to the animation frame request ID */
  const requestRef = React.useRef<number>();
  /** Reference to the previous animation frame timestamp */
  const previousTimeRef = React.useRef<number>();
  /** Reference to the current cursor coordinates */
  const coordsRef = React.useRef({ x: 0, y: 0 });
  /** Reference to the current cursor velocity */
  const velocityRef = React.useRef({ x: 0, y: 0 });
  /** State for cursor visibility */
  const [isVisible, setIsVisible] = React.useState(true);
  /** State for cursor active state (when clicking) */
  const [isActive, setIsActive] = React.useState(false);
  /** State for cursor active state on clickable elements */
  const [isActiveClickable, setIsActiveClickable] = React.useState(false);
  /** Reference to the target X coordinate */
  const endX = React.useRef(0);
  /** Reference to the target Y coordinate */
  const endY = React.useRef(0);
  /** State for play icon display */
  const [isPlayIcon, setIsPlayIcon] = useState(false);
  /** State for pause icon display */
  const [isPauseIcon, setIsPauseIcon] = useState(false);

  /**
   * Updates the target cursor position based on mouse movement
   * @param {MouseEvent} event - The mouse move event
   */
  const onMouseMove = React.useCallback(({ clientX, clientY }: MouseEvent) => {
    endX.current = clientX;
    endY.current = clientY;
  }, []);

  /**
   * Animates the outer cursor using spring physics
   * @param {number} time - Current timestamp for the animation frame
   */
  const animateOuterCursor = React.useCallback((time: number) => {
    if (previousTimeRef.current !== undefined && cursorOuterRef.current) {
      const deltaTime = time - previousTimeRef.current;
      const deltaTimeSeconds = deltaTime / 1000;

      // Spring configuration
      const stiffness = 300;
      const damping = 20;
      const mass = 1;

      const dx = endX.current - coordsRef.current.x;
      const dy = endY.current - coordsRef.current.y;

      const springForceX = stiffness * dx;
      const springForceY = stiffness * dy;

      const dampingForceX = damping * velocityRef.current.x;
      const dampingForceY = damping * velocityRef.current.y;

      const forceX = springForceX - dampingForceX;
      const forceY = springForceY - dampingForceY;

      const accelerationX = forceX / mass;
      const accelerationY = forceY / mass;

      const newVelocityX =
        velocityRef.current.x + accelerationX * deltaTimeSeconds;
      const newVelocityY =
        velocityRef.current.y + accelerationY * deltaTimeSeconds;

      const newX = coordsRef.current.x + newVelocityX * deltaTimeSeconds;
      const newY = coordsRef.current.y + newVelocityY * deltaTimeSeconds;

      velocityRef.current = { x: newVelocityX, y: newVelocityY };
      coordsRef.current = { x: newX, y: newY };

      cursorOuterRef.current.style.top = newY + "px";
      cursorOuterRef.current.style.left = newX + "px";
    }
    previousTimeRef.current = time;
    requestRef.current = requestAnimationFrame(animateOuterCursor);
  }, []);

  /**
   * Sets up and cleans up the animation frame loop
   */
  React.useEffect(() => {
    requestRef.current = requestAnimationFrame(animateOuterCursor);
    return () => {
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current);
      }
    };
  }, [animateOuterCursor]);

  /**
   * Handles mouse down event to set active state
   */
  const onMouseDown = React.useCallback(() => setIsActive(true), []);
  /**
   * Handles mouse up event to reset active state
   */
  const onMouseUp = React.useCallback(() => {
    setIsActive(false);
    setIsActiveClickable(false);
  }, []);
  /**
   * Handles mouse enter event to show cursor
   */
  const onMouseEnter = React.useCallback(() => setIsVisible(true), []);
  /**
   * Handles mouse leave event to hide cursor
   */
  const onMouseLeave = React.useCallback(() => setIsVisible(false), []);

  useEventListener(
    "mousemove",
    onMouseMove as (event: Event) => void,
    document,
  );
  useEventListener("mousedown", onMouseDown, document);
  useEventListener("mouseup", onMouseUp, document);
  useEventListener("mouseenter", onMouseEnter, document);
  useEventListener("mouseleave", onMouseLeave, document);

  /**
   * Updates cursor transform based on all active states
   */
  const updateCursorTransform = React.useCallback(() => {
    if (!cursorOuterRef.current) return;

    let scale = 1;
    
    if (isActiveClickable) {
      scale = outerScale * 1.4;
    } else if (isActive) {
      scale = outerScale;
    } else if (isPlayIcon || isPauseIcon) {
      scale = outerScale;
    }

    cursorOuterRef.current.style.transform = `translate(-50%, -50%) scale(${scale})`;
  }, [outerScale, isActive, isActiveClickable, isPlayIcon, isPauseIcon]);

  /**
   * Update transform when any relevant state changes
   */
  React.useEffect(() => {
    updateCursorTransform();
  }, [updateCursorTransform]);

  /**
   * Updates cursor visibility
   */
  React.useEffect(() => {
    if (isVisible && cursorOuterRef.current) {
      cursorOuterRef.current.style.opacity = "1";
    } else if (cursorOuterRef.current) {
      cursorOuterRef.current.style.opacity = "0";
    }
  }, [isVisible]);

  /**
   * Sets up event listeners for interactive elements and removes default cursor
   */
  React.useEffect(() => {
    if (!isBrowserDefined()) return;

    const clickables = document.querySelectorAll<HTMLElement>(
      'a, input[type="submit"], input[type="image"], label[for], select, button, .link, video, .play, .pause',
    );

    const handleMouseOver = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      
      // Reset all icon states first
      setIsPlayIcon(false);
      setIsPauseIcon(false);
      
      debugger;
      // Check for play elements
      if (target.classList.contains("play") || target.parentElement?.classList.contains('play')) {
        setIsPlayIcon(true);
      }
      // Check for pause elements
      else if (target.classList.contains("pause") || target.parentElement?.classList.contains('pause')) {
        setIsPauseIcon(true);
      }
      
      setIsActive(true);
    };

    const handleMouseClick = (e: MouseEvent) => {
      // Reset clickable state immediately so cursor shrinks from extra-large
      setIsActiveClickable(false);
      
      // For play/pause elements, handle the icon toggle
      if (isPlayIcon || isPauseIcon) {
        setIsPlayIcon(false);
        setIsPauseIcon(false);
        // Brief delay then restore the appropriate stat
      }
      setTimeout(() => {
        handleMouseOver(e);
      }, 200);
      // For regular buttons, just maintain the hover state
      // Don't reset isActive - let it stay true while hovering
    };

    const handleMouseDown = () => setIsActiveClickable(true);

    const handleMouseUp = () => {
      // Reset clickable state but maintain hover state briefly
      setIsActiveClickable(false);
    };

    const handleMouseOut = () => {
      // Reset all states when leaving an element
      setIsActive(false);
      setIsActiveClickable(false);
      setIsPlayIcon(false);
      setIsPauseIcon(false);
    };

    // Global event listener to reset cursor state
    const resetCursorState = () => {
      setIsActive(false);
      setIsActiveClickable(false);
      setIsPlayIcon(false);
      setIsPauseIcon(false);
    };

    document.addEventListener("mouseleave", resetCursorState);

    clickables.forEach((el) => {
      el.style.cursor = "none";
      el.addEventListener("mouseover", handleMouseOver);
      el.addEventListener("click", handleMouseClick);
      el.addEventListener("mousedown", handleMouseDown);
      el.addEventListener("mouseup", handleMouseUp);
      el.addEventListener("mouseout", handleMouseOut);
    });

    return () => {
      document.removeEventListener("mouseleave", resetCursorState);

      clickables.forEach((el) => {
        el.removeEventListener("mouseover", handleMouseOver);
        el.removeEventListener("click", handleMouseClick);
        el.removeEventListener("mousedown", handleMouseDown);
        el.removeEventListener("mouseup", handleMouseUp);
        el.removeEventListener("mouseout", handleMouseOut);
      });
    };
  }, [isPlayIcon, isPauseIcon]);

  // Determine cursor classes
  const getCursorClasses = () => {
    const baseClasses = "fixed rounded-full pointer-events-none transition-all duration-300 ease-out";
    if (isPauseIcon) return `${baseClasses} cb-cursor--pause`;
    if (isPlayIcon) return `${baseClasses} cb-cursor--play`;
    return baseClasses;
  };

  return (
    <div
      ref={cursorOuterRef}
      data-testid="animated-cursor"
      className={getCursorClasses()}
      style={{
        width: outerSize,
        height: outerSize,
        backgroundColor: `rgba(${color}, ${outerAlpha})`,
        zIndex: 999999999,
        transform: "translate(-50%, -50%)",
        willChange: "transform",
      }}
    />
  );
};

export default AnimatedCursor;